import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, Copy, Link2, Unplug } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { ConfigurationsCard } from "@/components/ConfigurationsCard";
import { BrandListField } from "@/components/BrandListField";
import { LayoutsCard } from "@/components/LayoutsCard";
import { BespokeSheetCard } from "@/components/BespokeSheetCard";


const CanvaCard = () => {
  const [st, setSt] = useState(null);
  const load = () => api.get("/canva/status").then((r) => setSt(r.data));
  useEffect(() => { load(); }, []);
  const connect = async () => {
    try { const { data } = await api.get("/canva/oauth/start"); window.location.href = data.authorization_url; }
    catch (e) { toast.error(errMsg(e)); }
  };
  const disconnect = async () => { await api.post("/canva/disconnect"); load(); };
  if (!st) return null;
  return (
    <section className="bg-[var(--cream)] p-8 page-shadow" data-testid="canva-card">
      <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--muted-ink)]">Integration</p>
      <h2 className="text-base md:text-lg font-bold mt-1">Canva sync</h2>
      <p className="text-sm text-[var(--muted-ink)] mt-2">Each product you add or edit is sent to your Canva account as its 2 pages. You can also send the whole catalog from a category.</p>
      <div className="mt-6 flex items-center gap-3 text-sm" data-testid="canva-connection-status">
        {st.connected ? <><CheckCircle2 size={16} className="text-green-700" />Connected to Canva</>
          : st.configured ? <>Ready to connect</> : <>Waiting for Canva app credentials</>}
      </div>
      {!st.configured && (
        <ol className="text-sm mt-4 space-y-2 list-decimal pl-5 text-[var(--muted-ink)]">
          <li>Create an integration at <a className="underline" href="https://www.canva.com/developers/integrations" target="_blank" rel="noreferrer">canva.com/developers</a>.</li>
          <li>Enable scopes: design:content:write, design:meta:read, asset:read, asset:write.</li>
          <li>Add this redirect URL:
            <span className="flex items-center gap-2 mt-1 bg-[var(--paper)] px-3 py-2 text-xs text-[var(--ink)] break-all" data-testid="canva-redirect-uri">{st.redirect_uri}
              <button onClick={() => { navigator.clipboard.writeText(st.redirect_uri); toast.success("Copied"); }}><Copy size={12} /></button></span></li>
          <li>Share the Client ID &amp; Client Secret with your developer so they're added to the server.</li>
        </ol>
      )}
      <div className="mt-6">
        {st.connected ? <Button variant="outline" onClick={disconnect} data-testid="canva-disconnect-button"><Unplug size={14} className="mr-2" />Disconnect</Button>
          : <Button disabled={!st.configured} onClick={connect} data-testid="canva-connect-button"><Link2 size={14} className="mr-2" />Connect Canva</Button>}
      </div>
    </section>
  );
};

export default function Settings() {
  const [brand, setBrand] = useState(null);
  const loc = useLocation();
  useEffect(() => { api.get("/settings").then((r) => setBrand(r.data)); }, []);
  useEffect(() => {
    const q = new URLSearchParams(loc.search).get("canva");
    if (q === "connected") toast.success("Canva connected");
    if (q === "error") toast.error("Canva connection failed");
  }, [loc.search]);
  const save = async () => {
    try {
      const { data: cur } = await api.get("/settings");
      const { data } = await api.put("/settings", { ...cur, brand_name: brand.brand_name, brands: brand.brands, show_prices: brand.show_prices });
      setBrand(data); toast.success("Brand details saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-12 rise">Settings</h1>
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <section className="bg-[var(--cream)] p-8 page-shadow" data-testid="brand-card">
          <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--muted-ink)]">Every catalog</p>
          <h2 className="text-base md:text-lg font-bold mt-1">Brands, SKU codes &amp; contact details</h2>
          {brand && <div className="space-y-4 mt-6">
            <BrandListField brand={brand} setBrand={setBrand} />
            <label className="flex items-center gap-3 text-sm pt-2" data-testid="settings-price-toggle">
              <Switch checked={brand.show_prices !== false} onCheckedChange={(v) => setBrand({ ...brand, show_prices: v })} data-testid="settings-price-switch" />
              {brand.show_prices !== false ? "Show prices in catalogs" : "Prices hidden: catalogs say “On request”"}
            </label>
            <Button onClick={save} data-testid="save-brand-button">Save</Button>
          </div>}
        </section>
        <CanvaCard />
        <ConfigurationsCard />
        <BespokeSheetCard />
        <LayoutsCard />
      </div>
    </div>
  );
}
